import { useCallback, useMemo, useState } from 'react'
import { AlertCircle, BookCheck, CheckCircle2, DoorOpen, Eye, Lock, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { confirmDialog } from '../lib/dialogs'
import { formatCents } from '../lib/money'

interface YearCloseLine {
  position: number
  accountCode: string
  description: string
  debitCents: number
  creditCents: number
}

interface YearCloseResult {
  year: number
  entryDate: string
  number: number
  source: 'year_close'
  totalIncomeCents: number
  totalExpenseCents: number
  resultCents: number
  lines: YearCloseLine[]
  totalDebitCents: number
  totalCreditCents: number
  alreadyClosed: boolean
  entryId: string | null
}

/** F1 — Resultado de la apertura de ejercicio (mismo contrato que el cierre). */
interface YearOpenResult {
  year: number
  entryDate: string
  number: number
  source: 'year_open'
  fromYear: number
  lines: YearCloseLine[]
  totalDebitCents: number
  totalCreditCents: number
  reclassifiedResultCents: number
  alreadyOpened: boolean
  entryId: string | null
}

const CURRENT_YEAR = new Date().getFullYear()

export default function AccountingClose() {
  const { t } = useTranslation('accounting')
  const [year, setYear] = useState<number>(CURRENT_YEAR - 1)
  const [preview, setPreview] = useState<YearCloseResult | null>(null)
  const [confirmed, setConfirmed] = useState<YearCloseResult | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [loadingConfirm, setLoadingConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR; y >= CURRENT_YEAR - 7; y--) arr.push(y)
    return arr
  }, [])

  const explainError = (err: unknown): string =>
    err instanceof ApiError
      ? ((err.body as { error?: string })?.error ?? err.message)
      : err instanceof Error
        ? err.message
        : t('unexpectedError')

  const onPreview = useCallback(async () => {
    setLoadingPreview(true)
    setError(null)
    setConfirmed(null)
    try {
      const data = await api.post<YearCloseResult>(
        `/accounting/close-year?year=${year}&dryRun=true`,
      )
      setPreview(data)
    } catch (err) {
      setError(explainError(err))
      setPreview(null)
    } finally {
      setLoadingPreview(false)
    }
  }, [year])

  const onConfirm = useCallback(async () => {
    if (!preview) return
    if (!(await confirmDialog({ message: t('confirmDialog', { year: preview.year }), tone: 'danger' })))
      return
    setLoadingConfirm(true)
    setError(null)
    try {
      const data = await api.post<YearCloseResult>(
        `/accounting/close-year?year=${year}&dryRun=false`,
      )
      setConfirmed(data)
      setPreview(null)
    } catch (err) {
      setError(explainError(err))
    } finally {
      setLoadingConfirm(false)
    }
  }, [preview, year])

  const result = confirmed ?? preview
  const isConfirmed = confirmed !== null

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <BookCheck size={20} className="text-toga-700 dark:text-toga-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
      </header>

      <section
        className="rounded-xl border p-4 flex flex-wrap items-end gap-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('year')}
          <select
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value))
              setPreview(null)
              setConfirmed(null)
              setError(null)
            }}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={onPreview}
          disabled={loadingPreview || loadingConfirm}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 disabled:opacity-50 text-white text-sm"
        >
          {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          {t('previewButton', { year })}
        </button>

        {preview && !isConfirmed && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={loadingConfirm || preview.alreadyClosed}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm"
          >
            {loadingConfirm ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            {t('confirmButton')}
          </button>
        )}
      </section>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <section
          className="rounded-xl border p-4 space-y-4"
          style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
        >
          <header className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm t-primary">
              <div className="font-semibold">
                {isConfirmed
                  ? t('confirmedHeader', { year: result.year })
                  : t('previewHeader', { year: result.year })}
              </div>
              <div className="text-xs t-muted">
                {t('entryMeta', {
                  number: result.number,
                  date: result.entryDate.slice(0, 10),
                  source: result.source,
                })}
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="t-muted text-xs">{t('resultLabel')}</div>
              <div
                className={
                  result.resultCents > 0
                    ? 'text-emerald-700 dark:text-emerald-300 font-mono font-semibold'
                    : result.resultCents < 0
                      ? 'text-rose-700 dark:text-rose-300 font-mono font-semibold'
                      : 'font-mono t-primary'
                }
              >
                {formatCents(result.resultCents)}{' '}
                {result.resultCents > 0
                  ? t('profit')
                  : result.resultCents < 0
                    ? t('loss')
                    : ''}
              </div>
            </div>
          </header>

          {result.alreadyClosed && !isConfirmed && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-200">
              {t('alreadyClosed')}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div
              className="rounded-md border p-3"
              style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
            >
              <div className="text-xs t-muted">{t('totalIncome')}</div>
              <div className="font-mono">{formatCents(result.totalIncomeCents)}</div>
            </div>
            <div
              className="rounded-md border p-3"
              style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
            >
              <div className="text-xs t-muted">{t('totalExpense')}</div>
              <div className="font-mono">{formatCents(result.totalExpenseCents)}</div>
            </div>
            <div
              className="rounded-md border p-3"
              style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
            >
              <div className="text-xs t-muted">{t('totalDebit')}</div>
              <div className="font-mono">{formatCents(result.totalDebitCents)}</div>
            </div>
            <div
              className="rounded-md border p-3"
              style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
            >
              <div className="text-xs t-muted">{t('totalCredit')}</div>
              <div className="font-mono">{formatCents(result.totalCreditCents)}</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs t-muted uppercase tracking-wider"
                  style={{ borderBottom: '1px solid var(--border)' }}
                >
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">{t('table.account')}</th>
                  <th className="py-2 pr-2">{t('table.description')}</th>
                  <th className="py-2 pr-2 text-right">{t('table.debit')}</th>
                  <th className="py-2 pr-2 text-right">{t('table.credit')}</th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l) => (
                  <tr
                    key={l.position}
                    style={{ borderBottom: '1px solid var(--border)' }}
                    className="t-primary"
                  >
                    <td className="py-1.5 pr-2 font-mono text-xs t-muted">{l.position + 1}</td>
                    <td className="py-1.5 pr-2 font-mono">{l.accountCode}</td>
                    <td className="py-1.5 pr-2">{l.description}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">
                      {l.debitCents ? formatCents(l.debitCents) : ''}
                    </td>
                    <td className="py-1.5 pr-2 text-right font-mono">
                      {l.creditCents ? formatCents(l.creditCents) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td className="pt-2" />
                  <td className="pt-2" />
                  <td className="pt-2 text-xs t-muted text-right">{t('table.totals')}</td>
                  <td className="pt-2 text-right font-mono font-semibold">
                    {formatCents(result.totalDebitCents)}
                  </td>
                  <td className="pt-2 text-right font-mono font-semibold">
                    {formatCents(result.totalCreditCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {isConfirmed && result.entryId && (
            <div className="text-xs t-muted">
              {t('entryCreated')} <span className="font-mono">{result.entryId}</span>.
            </div>
          )}
        </section>
      )}

      <YearOpenPanel />
    </div>
  )
}

/**
 * F1 — APERTURA DE EJERCICIO. Panel independiente del cierre: mismo ciclo
 * previsualizar → confirmar, pero su propio estado para no interferir.
 */
function YearOpenPanel() {
  const { t } = useTranslation('accounting')
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [preview, setPreview] = useState<YearOpenResult | null>(null)
  const [confirmed, setConfirmed] = useState<YearOpenResult | null>(null)
  const [loading, setLoading] = useState<'preview' | 'confirm' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y -= 1) arr.push(y)
    return arr
  }, [])

  const explainError = (err: unknown): string => {
    if (err instanceof ApiError) {
      const body = err.body as { error?: string } | undefined
      return body?.error ?? err.message
    }
    return err instanceof Error ? err.message : t('unexpectedError')
  }

  const run = useCallback(
    async (dryRun: boolean) => {
      setLoading(dryRun ? 'preview' : 'confirm')
      setError(null)
      try {
        const data = await api.post<YearOpenResult>(
          `/accounting/open-year?year=${year}&dryRun=${dryRun}`
        )
        if (dryRun) {
          setPreview(data)
          setConfirmed(null)
        } else {
          setConfirmed(data)
        }
      } catch (err) {
        setError(explainError(err))
        if (dryRun) setPreview(null)
      } finally {
        setLoading(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year]
  )

  const result = confirmed ?? preview
  const isConfirmed = confirmed !== null

  return (
    <section
      className="rounded-xl border p-4 space-y-4"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
    >
      <header>
        <h2 className="text-sm font-semibold t-primary flex items-center gap-2">
          <DoorOpen size={16} className="text-toga-700 dark:text-toga-300" />
          {t('open.title')}
        </h2>
        <p className="text-xs t-muted mt-0.5 max-w-3xl">{t('open.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('open.year')}
          <select
            value={year}
            onChange={(e) => {
              setYear(Number(e.target.value))
              setPreview(null)
              setConfirmed(null)
              setError(null)
            }}
            className="rounded-md border px-2 py-1.5 text-sm"
            style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => void run(true)}
          disabled={loading !== null}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 disabled:opacity-50 text-white text-sm"
        >
          {loading === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
          {t('open.previewButton', { year })}
        </button>

        {preview && !isConfirmed && !preview.alreadyOpened && (
          <button
            type="button"
            onClick={() => void run(false)}
            disabled={loading !== null}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm"
          >
            {loading === 'confirm' ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <DoorOpen size={14} />
            )}
            {t('open.confirmButton')}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {isConfirmed ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 size={15} /> {t('open.created', { year: result.year })}
              </span>
            ) : result.alreadyOpened ? (
              <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                <AlertCircle size={15} /> {t('open.alreadyOpened', { year: result.year })}
              </span>
            ) : (
              <span className="t-muted text-xs">
                {t('open.previewOf', { from: result.fromYear, year: result.year })}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider t-faint">
                {t('open.linesLabel')}
              </div>
              <div className="font-mono t-primary">{result.lines.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider t-faint">{t('totalDebit')}</div>
              <div className="font-mono t-primary">{formatCents(result.totalDebitCents)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider t-faint">{t('totalCredit')}</div>
              <div className="font-mono t-primary">{formatCents(result.totalCreditCents)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider t-faint">
                {t('open.reclassified')}
              </div>
              <div className="font-mono t-primary">
                {formatCents(result.reclassifiedResultCents)}
              </div>
            </div>
          </div>

          {result.reclassifiedResultCents !== 0 && (
            <p className="text-xs t-muted">{t('open.reclassifiedHint')}</p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                  <th className="py-1">{t('table.account')}</th>
                  <th className="py-1">{t('table.description')}</th>
                  <th className="py-1 text-right">{t('table.debit')}</th>
                  <th className="py-1 text-right">{t('table.credit')}</th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l) => (
                  <tr key={`${l.position}-${l.accountCode}`} className="border-t border-base/40">
                    <td className="py-1 font-mono text-xs">{l.accountCode}</td>
                    <td className="py-1 t-secondary">{l.description}</td>
                    <td className="py-1 text-right font-mono">
                      {l.debitCents ? formatCents(l.debitCents) : ''}
                    </td>
                    <td className="py-1 text-right font-mono">
                      {l.creditCents ? formatCents(l.creditCents) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isConfirmed && result.entryId && (
            <div className="text-xs t-muted">
              {t('entryCreated')} <span className="font-mono">{result.entryId}</span>.
            </div>
          )}
        </div>
      )}
    </section>
  )
}
